import { useFeatureFlagsStore } from '@renderer/stores/featureFlagsStore'

/**
 * Camera Auto-Grant (flag `camera_auto_grant`, Phase 3 of docs/camera-rest-plan.md).
 *
 * The renderer opts the MAIN process into a WebUSB device-permission handler that
 * auto-grants camera-class devices, so `navigator.usb.getDevices()` returns the
 * camera at app boot and after a physical replug — the two cases Reconnect v2
 * cannot cover (the in-memory chooser grant does not survive either). Combined
 * with Reconnect v2, connection becomes fully gesture-free: no "Click to Start
 * Camera" prompt, ever.
 *
 * Gated in BOTH processes: main installs the handler only after receiving this
 * opt-in message, and this module only sends `enabled: true` while the flag is on.
 * Old packaged mains have no listener on the channel → harmless no-op → behavior
 * falls back to Reconnect v2 / legacy. The sync is idempotent and re-fires on
 * every flag change (including the post-login fetch and offline hydration), so a
 * flag flip in Studio takes effect on the booth's next flag refresh.
 */
export const CAMERA_AUTO_GRANT_FLAG = 'camera_auto_grant'

export function isAutoGrantEnabled(): boolean {
  return useFeatureFlagsStore.getState().getFlag(CAMERA_AUTO_GRANT_FLAG, false)
}

let lastSent: boolean | null = null
let initialized = false

function sendOptIn(enabled: boolean): void {
  if (typeof window === 'undefined' || !window.electron || typeof window.api?.send !== 'function') {
    return
  }
  if (lastSent === enabled) return
  lastSent = enabled
  console.log(`[USB-AUTO-GRANT] Sending main-process opt-in: ${enabled}`)
  window.api.send('set-usb-auto-grant', { enabled })
}

/** Idempotent — call once at app bootstrap (Electron only; no-op on web). */
export function initUsbAutoGrantSync(): void {
  if (initialized) return
  initialized = true
  sendOptIn(isAutoGrantEnabled())
  useFeatureFlagsStore.subscribe(() => sendOptIn(isAutoGrantEnabled()))
}
